<template>
  <div class="flex flex-row items-center gap-0.5 flex-shrink-0">
<!-- Health: a green dot when all is well, a warning icon in the worst failure's colour otherwise -->
    <Popper hover arrow placement="bottom-start">
      <button type="button" class="topbar-btn" :aria-label="healthLabel">
        <span v-if="!healthSeverity" class="topbar-health-ok" aria-hidden="true"></span>
        <!-- Class names written out whole: Tailwind drops a component class it never sees in full -->
        <ExclamationIcon v-else :class="{ 'topbar-sev-red': healthSeverity === 'red', 'topbar-sev-orange': healthSeverity === 'orange', 'topbar-sev-amber': healthSeverity === 'amber' }" />
      </button>
      <template #content>
        <div class="flex flex-col gap-0.5 text-left">
          <div class="flex flex-row items-center gap-2">
            <span class="topbar-dot" :class="appStatus?.dbConnection ? 'topbar-dot-ok' : 'topbar-dot-red'"></span>
            <span class="font-semibold w-16">Database</span>
            <span>{{ appStatus?.dbConnection ? 'Connected' : 'Disconnected' }}</span>
          </div>
          <div class="flex flex-row items-center gap-2">
            <span class="topbar-dot" :class="appStatus?.wsConnection ? 'topbar-dot-ok' : 'topbar-dot-orange'"></span>
            <span class="font-semibold w-16">Sockets</span>
            <span>{{ appStatus?.wsConnection ? 'Connected' : 'Disconnected: live updates paused' }}</span>
          </div>
          <div class="flex flex-row items-center gap-2">
            <span class="topbar-dot" :class="appStatus?.aiConnection ? 'topbar-dot-ok' : 'topbar-dot-amber'"></span>
            <span class="font-semibold w-16">AI</span>
            <span>{{ appStatus?.aiConnection ? 'Reachable' : 'Degraded: retries may be slower' }}</span>
          </div>
        </div>
      </template>
    </Popper>
<!-- Scrollbars -->
    <Popper hover arrow placement="bottom" :content="scrollBars ? 'Hide scrollbars' : 'Show scrollbars'">
      <button type="button" class="topbar-btn" :aria-label="scrollBars ? 'Hide scrollbars' : 'Show scrollbars'" :aria-pressed="scrollBars" @click="toggleScrollBars()"><SelectorIcon /></button>
    </Popper>
<!-- Dark Mode Toggle -->
    <Popper hover arrow placement="bottom" :content="darkMode ? 'Switch to light' : 'Switch to dark'">
      <button type="button" class="topbar-btn" :aria-label="darkMode ? 'Switch to light' : 'Switch to dark'" @click="toggleTheme()">
        <MoonIcon v-if="darkMode" />
        <SunIcon v-else />
      </button>
    </Popper>
  </div>
</template>

<script>
import {ExclamationIcon} from '@heroicons/vue/outline'
import {SunIcon,MoonIcon,SelectorIcon} from "@heroicons/vue/solid"
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
export default {
    components:{
        ExclamationIcon,
        SunIcon,
        MoonIcon,
        SelectorIcon
    },
    setup() {
        const global = inject('global')
        const {
            authenticated,
            appStatus,
            darkState,
            scrollState,
            setTheme,
            toggleTheme,
            setScrollBars,
            toggleScrollBars,
            setAiAssistCollapsed,
        } = global
        
        const darkMode = ref(darkState)
        const scrollBars = ref(scrollState)

        onMounted(()=>{
            window.addEventListener('keydown',handleKeydown)
            window.addEventListener("data_change_from_socket",handleSocketChange)
        })
        
        onBeforeUnmount(()=>{
            window.removeEventListener('keydown',handleKeydown)
            window.removeEventListener('data_change_from_socket',handleSocketChange)
        })

        const healthSeverity = computed(()=>{
            if (!appStatus.value?.dbConnection) return 'red'
            if (!appStatus.value?.wsConnection) return 'orange'
            if (!appStatus.value?.aiConnection) return 'amber'
            return null
        })

        const healthLabel = computed(()=>{
            if (!healthSeverity.value) return 'All services connected'
            const problems = []
            if (!appStatus.value?.dbConnection) problems.push('database disconnected')
            if (!appStatus.value?.wsConnection) problems.push('sockets disconnected: live updates paused')
            if (!appStatus.value?.aiConnection) problems.push('AI degraded: retries may be slower')
            return 'Service problem: ' + problems.join(', ')
        })

        const handleKeydown = (event)=>{
            if (event.ctrlKey && event.shiftKey && event.key === 'D') {
                toggleTheme();
            } else if (event.ctrlKey && event.shiftKey && event.key === 'S') {
                toggleScrollBars();
            }
        }

        const handleSocketChange = (e)=>{
            if(e.detail?.type) {
                if(e.detail.type === 'themeSettings') {
                    if(e.detail?.data?.userId && e.detail?.data?.userId === authenticated.value._id ) {
                        if(e.detail?.data?.darkModeState) {
                            setTheme(e.detail.data.darkModeState)
                            try { localStorage.setItem('theme', e.detail.data.darkModeState) } catch { /* storage blocked: the theme still applies */ }
                        }

                        if(e.detail?.data && typeof e.detail.data === 'object' && Object.prototype.hasOwnProperty.call(e.detail.data,'scrollBarState')) {
                            setScrollBars(e.detail.data.scrollBarState)
                            try { localStorage.setItem('scrollBars', e.detail.data.scrollBarState ? 'true' : 'false') } catch { /* storage blocked */ }
                        }

                        if(e.detail?.data && typeof e.detail.data === 'object' && Object.prototype.hasOwnProperty.call(e.detail.data,'aiAssistCollapsed')) {
                            setAiAssistCollapsed(e.detail.data.aiAssistCollapsed)
                            try { localStorage.setItem('aiAssistCollapsed', e.detail.data.aiAssistCollapsed ? 'true' : 'false') } catch { /* storage blocked */ }
                        }
                    }
                }
            }
        }

        return {
            appStatus,
            darkState,
            scrollState,
            setTheme,
            toggleTheme,
            setScrollBars,
            toggleScrollBars,
            darkMode,
            scrollBars,
            healthSeverity,
            healthLabel
        }

    }
}
</script>

<style>

</style>